import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
  Box,
  Typography,
  Chip,
  IconButton
} from '@mui/material';
import MinimizeIcon from '@mui/icons-material/Minimize';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import SystemAlertStyles from '../styles/SystemAlert.module.css';

const SystemAlertModal = ({ open, alerts, onMinimize }) => {
  const [currentAlertIndex, setCurrentAlertIndex] = useState(0);

  if (!alerts || alerts.length === 0) {
    return null;
  }

  const currentAlert = alerts[currentAlertIndex];
  const hasMultipleAlerts = alerts.length > 1;

  const handleNext = () => {
    setCurrentAlertIndex((prev) => (prev + 1) % alerts.length);
  };

  const handlePrevious = () => {
    setCurrentAlertIndex((prev) => (prev - 1 + alerts.length) % alerts.length);
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onMinimize}
      maxWidth="md"
      fullWidth
      classes={{ paper: SystemAlertStyles.dialogPaper }}
    >
      <DialogTitle className={SystemAlertStyles.dialogTitle}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" component="span" className={SystemAlertStyles.titleText}>
              System Alert
            </Typography>
            <Chip
              label={currentAlert.alertType}
              color={currentAlert.alertType === 'FAA' ? 'error' : 'warning'}
              size="small"
              className={SystemAlertStyles.alertTypeChip}
            />
          </Box>
          <IconButton
            aria-label="minimize"
            onClick={onMinimize}
            className={SystemAlertStyles.minimizeButton}
            size="small"
          >
            <MinimizeIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent className={SystemAlertStyles.dialogContent}>
        {/* Time Range */}
        <Box className={SystemAlertStyles.timeRange}>
          <Typography variant="body2" color="text.secondary">
            <strong>Effective Period:</strong>
          </Typography>
          <Typography variant="body2">
            {formatDateTime(currentAlert.startTime)} - {formatDateTime(currentAlert.endTime)}
          </Typography>
        </Box>

        {/* Alert Message */}
        <Box
          className={SystemAlertStyles.messageContent}
          dangerouslySetInnerHTML={{ __html: currentAlert.message }}
        />

        {/* Multiple alerts indicator */}
        {hasMultipleAlerts && (
          <Box className={SystemAlertStyles.alertCounter}>
            <Typography variant="caption" color="text.secondary">
              Alert {currentAlertIndex + 1} of {alerts.length}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className={SystemAlertStyles.dialogActions}>
        {/* Navigation buttons for multiple alerts */}
        {hasMultipleAlerts && (
          <Box className={SystemAlertStyles.navigationButtons}>
            <IconButton
              onClick={handlePrevious}
              disabled={currentAlertIndex === 0}
              size="small"
            >
              <NavigateBeforeIcon />
            </IconButton>
            <IconButton
              onClick={handleNext}
              disabled={currentAlertIndex === alerts.length - 1}
              size="small"
            >
              <NavigateNextIcon />
            </IconButton>
          </Box>
        )}

        <Box className={SystemAlertStyles.actionButtons}>
          <Button
            onClick={onMinimize}
            variant="contained"
            className={SystemAlertStyles.minimizeButtonAction}
          >
            Minimize
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SystemAlertModal;
